<template>
  <v-container>
    <v-layout row wrap>
      <v-flex xs12 sm12 md5 class="pa-3">
        <!-- Movie Poster (If available) -->
        <v-img v-if="film.thumbs.length" :src="poster()" width="100%" contain></v-img>
        <!-- If poster is not available -->
        <v-img v-else src="@/assets/no-image-available.jpg" width="100%" contain></v-img>
      </v-flex>
      <v-flex xs12 sm12 md7 class="pa-3">
        <!-- title and type -->
        <v-layout row wrap class="mb-4">
          <v-flex xs12>
            <div class="display-1">{{film.title}}</div>
          </v-flex>
        </v-layout>
        <!-- Film rights and add_to_wishlist btn -->
        <v-layout row wrap class="my-2">
          <v-flex xs12 sm6 class="pa-1">
            <v-btn
              large
              block
              outline
              color="green lighten-2"
              @click="filmrights_dialog = true"
              :disabled="!isRightsOrCountriesApplied"
            >
              <v-icon left>{{ $vuetify.icons.rms_view }}</v-icon>
              {{ $t('rms.film.film_rights_availability_btn_label') }}
            </v-btn>
          </v-flex>
          <v-flex xs12 sm6 class="pa-1">
            <v-btn
              large
              :disabled="session_mode !== DEFINES.AVAIL_SESSION_MODE"
              outline
              block
              color="pink"
              @click="addIfNotExist(film.id)"
            >
              <v-icon left>{{$vuetify.icons.rms_favorite_filled}}</v-icon>
              &ensp;{{$t('rms.add.to.wishlist.btn.label')}}
            </v-btn>
          </v-flex>
        </v-layout>
        <v-divider class="my-2"></v-divider>
        <!-- Description -->
        <v-layout row wrap v-if="film.description">
          <v-flex xs4 sm4 md3 class="orange--text font-weight-bold">{{$t('rms_desc_label')}}</v-flex>
          <v-flex xs8 sm8 md9>
            <span v-if="activate_read_more">{{ film.description.slice(0,50) }}&nbsp;</span>
            <span v-if="activate_read_less">{{ film.description }}&nbsp;</span>
            <span
              class="blue--text"
              v-if="activate_read_more"
              @click="processReadMore()"
            >{{ $t('rms_show_more_label') }}</span>
            <span
              class="blue--text"
              v-if="activate_read_less"
              @click="processReadLess()"
            >{{ $t('rms_show_less_label') }}</span>
          </v-flex>
        </v-layout>
        <!-- Type -->
        <v-layout row wrap v-if="film.type" class="mt-3">
          <v-flex xs4 sm4 md3 class="orange--text font-weight-bold">{{ $t('rms_type_label') }}</v-flex>
          <v-flex xs8 sm8 md9>{{ getTagName(film.type, this.DEFINES.TAG_TYPE.FOR_FILM_TYPE) }}</v-flex>
        </v-layout>
        <!-- Genres -->
        <v-layout row wrap v-if="film.genres && film.genres.length" class="mt-3">
          <v-flex xs4 sm4 md3 class="orange--text font-weight-bold">{{ $t('rms_genre_label') }}</v-flex>
          <v-flex xs8 sm8 md9>
            <span
              v-for="(genre,index) in getTags(this.DEFINES.TAG_TYPE.FOR_FILM_GENRE,film.genres)"
              :key="genre.id"
            >
              {{ genre.name }}
              <span v-if="isShowDivider(index+1,film.genres)">,&ensp;</span>
            </span>
          </v-flex>
        </v-layout>
        <!-- Languages -->
        <v-layout row wrap v-if="film.languages && film.languages.length" class="mt-3">
          <v-flex
            xs4
            sm4
            md3
            class="orange--text font-weight-bold"
          >{{ $t('avails.film.overview.label.languages') }}</v-flex>
          <v-flex xs8 sm8 md9>
            <span
              v-for="(language,index) in getTags(this.DEFINES.TAG_TYPE.FOR_LANGUAGES,film.languages)"
              :key="language.id"
            >
              {{language.name}}
              <span v-if="isShowDivider(index+1,film.languages)">,&ensp;</span>
            </span>
          </v-flex>
        </v-layout>
        <!-- Artists -->
        <v-layout row wrap v-if="film.artists && film.artists.length" class="mt-3">
          <v-flex
            xs4
            sm4
            md3
            class="orange--text font-weight-bold"
          >{{ $t('avails.film.overview.label.artists') }}</v-flex>
          <v-flex xs8 sm8 md9>
            <span
              v-for="(artist,index) in getTags(this.DEFINES.TAG_TYPE.FOR_FILM_ARTIST,film.artists)"
              :key="artist.id"
            >
              {{ artist.name }}
              <span v-if="isShowDivider(index+1,film.artists)">,&ensp;</span>
            </span>
          </v-flex>
        </v-layout>
        <!-- Keywords -->
        <v-layout row wrap v-if="film.keywords.length" class="mt-3">
          <v-flex
            xs4
            sm4
            md3
            class="orange--text font-weight-bold"
          >{{ $t('avails.film.overview.label.keywords') }}</v-flex>
          <v-flex xs8 sm8 md9>
            <span
              v-for="(keyword,index) in getTags(DEFINES.TAG_TYPE.FOR_FILM_RANDOM,film.keywords)"
              :key="keyword.id"
            >
              {{ keyword.name }}
              <span v-if="isShowDivider(index+1,film.keywords)">,&ensp;</span>
            </span>
          </v-flex>
        </v-layout>
        <!-- Release Date -->
        <v-layout row wrap v-if="film.released_at" class="mt-3">
          <v-flex
            xs4
            sm4
            md3
            class="orange--text font-weight-bold"
          >{{ $t('avails.film.overview.label.released_date') }}</v-flex>
          <v-flex xs8 sm8 md9>
            <span>{{ displayReleasedDate(film.released_at) }}</span>
          </v-flex>
        </v-layout>
        <!-- Length -->
        <v-layout row wrap v-if="film.length" class="mt-3">
          <v-flex
            xs4
            sm4
            md3
            class="orange--text font-weight-bold"
          >{{ $t('avails.film.overview.label.runtime') }}</v-flex>
          <v-flex xs8 sm8 md9>
            <span>{{ film.length }}</span>
          </v-flex>
        </v-layout>
        <!-- Format -->
        <v-layout row wrap v-if="film.format">
          <v-flex
            xs4
            sm4
            md3
            class="orange--text font-weight-bold"
          >{{ $t('avails.film.overview.label.format') }}</v-flex>
          <v-flex xs8 sm8 md9>
            <span>{{ getTagName(film.format,DEFINES.TAG_TYPE.FOR_FILM_FORMAT) }}</span>
          </v-flex>
        </v-layout>
        <!-- TRAILERS -->
        <Medias
          v-if="film.trailers && film.trailers.length"
          :media_type="DEFINES.MEDIA.FILM.TRAILERS"
          :file_type="DEFINES.MIME_TYPE.VIDEO"
          :film_id="film.id"
        ></Medias>

        <!-- SCREENERS -->
        <Medias
          v-if="shouldShowScreeners()"
          :media_type="DEFINES.MEDIA.FILM.SCREENERS"
          :file_type="DEFINES.MIME_TYPE.VIDEO"
          :film_id="film.id"
        ></Medias>

        <!-- Film-Rights -->
        <FilmRightsOverview
          v-model="filmrights_dialog"
          :filmrights_dialog="filmrights_dialog"
          v-if="filmrights_dialog"
          :film="film"
        ></FilmRightsOverview>
      </v-flex>
    </v-layout>
  </v-container>
</template>
<script>
import FilmRightsOverview from "@/components/filmrights/overview/Index";
import { mapGetters, mapState } from "vuex";
import i18n from "@/i18n";
import Medias from "@/components/film/Medias";

export default {
  name: "FilmOverview",
  data() {
    return {
      dialog: false,
      not_allowed_message: null,
      filmrights_dialog: false,
      activate_read_more: true,
      activate_read_less: false
    };
  },

  components: {
    Medias,
    FilmRightsOverview
  },
  computed: {
    ...mapState({
      session_mode: state => state.app.session_mode
    }),
    film: function() {
      return this.$store.getters["film/getFilm"](this.$route.params.film_id);
    },
    genres: function() {
      return this.$store.getters["tag/getTagsfromIds"](
        this.film.genres,
        this.DEFINES.TAG_TYPE.FOR_FILM_GENRE
      );
    },
    ...mapGetters({
      is_filters_applied: "filter/is_applied",
      isRightsOrCountriesApplied: "filter/isRightsOrCountriesApplied",
      filters: "filter/getApplied",
      wishlist: "wishlist/getList",
      availLink: "availLink/get"
    })
  },
  mounted() {
    this.setToolbarHeader();
  },
  methods: {
    processReadMore() {
      this.activate_read_more = false;
      this.activate_read_less = true;
    },

    processReadLess() {
      this.activate_read_more = true;
      this.activate_read_less = false;
    },

    async setToolbarHeader() {
      await this.$store.dispatch("app/setToolbarHeader", {
        header: i18n.t("rms.toolbar.header.movie_overview")
      });
    },

    available(available_value) {
      return Math.round(available_value);
    },

    poster: function() {
      return this.film.thumburls[this.film.thumbs[0]]; // Returning first thumbnail
    },

    getTagName(tag_id, tag_type) {
      var tag = this.$store.getters["tag/getTag"](tag_id, tag_type);
      return tag.name;
    },

    getTags(type, tags) {
      var tags = this.$store.getters["tag/getTagsfromIds"](tags, type);
      return tags;
    },

    async addIfNotExist(film_id) {
      var to_be_added = this.makePayloadForWishlist(film_id);
      if (this.wishlist) {
        var exist = this.wishlist.filter(function(entry) {
          return Object.keys(to_be_added).every(function(key) {
            return (
              JSON.stringify(entry[key]) === JSON.stringify(to_be_added[key])
            );
          });
        }).length;

        if (exist) {
          this.$store.dispatch("app/showMessage", {
            color: "error",
            message: "rms.wishlist.movie_already_added_with_same_filters"
          });
          return;
        }
      }
      this.addToWishlist(to_be_added);
    },

    makePayloadForWishlist(film_id) {
      var payload = {
        film_id: film_id,
        rights: this.filters.rights ? this.filters.rights : [],
        countries: this.filters.countries ? this.filters.countries : [],
        exclusive: this.filters.exclusive
      };
      return payload;
    },

    async addToWishlist(payload) {
      await this.$store.dispatch("wishlist/add", {
        film: payload
      });
    },

    shouldShowScreeners() {
      // If session is in avail mode then check permission
      if (
        this.session_mode == this.DEFINES.AVAIL_SESSION_MODE &&
        !this.availLink.is_screener_allowed
      ) {
        this.not_allowed_message = i18n.t(
          "rms.availLink.if_not_allowed_to_access_screeners"
        );
        return false;
      }
      if (!this.film.screeners || this.film.screeners.length == 0) {
        this.not_allowed_message = i18n.t("rms.film.screeners_not_available");
        return false;
      }

      return true;
    },

    // FIXME::Handel in better way
    displayReleasedDate(date) {
      var year_diff = Math.round(this.moment().diff(date, "years", true));

      let released_date = null;
      switch (year_diff) {
        case 0:
          released_date = i18n.t("rms.film_released_date.this_year_label");
          break;
        case 1:
          released_date = i18n.t("rms.film_released_date.previous_year_label");
          break;
        case 2:
        case 3:
          released_date = this.moment(date).fromNow();
          break;
        default:
          released_date = i18n.t(
            "rms.film_released_date.older_than_3_years_label"
          );
      }
      return released_date;
    },
    isShowDivider(index, array) {
      return index > 0 && index < array.length;
    }
  }
};
</script>

<style scoped>
.media_file_container {
  height: 100%;
  position: relative;
  width: 100%;
}
</style>
